import { useEffect, useRef } from 'react';
import { BroadcastChannel } from 'broadcast-channel';

import type { AppleAuthConfig } from 'src/components/AuthNew/utils/socialNetworkUtils';

const createAppleAuthUrl = (config: AppleAuthConfig) => {
  const params = [
    `client_id=${config.credentials.clientId}`,
    'response_type=code%20id_token',
    'response_mode=form_post',
    `redirect_uri=${window.location.origin}/login/apple`,
    'scope=name%20email',
  ].join('&');

  return ['https://appleid.apple.com/auth/authorize', params].join('?');
};

type AppleAuthFailPayload = {
  error?: string;
  state?: string;
};

type AppleAuthSuccessPayload = {
  code?: string;
  user?: string;
  id_token?: string;
  state?: string;
};

export type AppleAuthPayload = AppleAuthFailPayload & AppleAuthSuccessPayload;

type AppleAuthResult = {
  success: boolean;
  payload: AppleAuthPayload;
};

type AppleAuthOnSuccess = (result: AppleAuthResult) => void;
type AppleAuthOnError = () => void;

/**
 * @desc процесс авторизации следующий:
 * 1. в новом окне открывается ссылка для авторизации на стороне Apple
 * 2. создается Broadcast канал для обмена сообщениями между окном инициатором авторизации
 * и окном, где происходит запрос кода Apple
 * 3. после подтверждения доступа, Apple перенаправляет на redirect_url из ссылки и добавляет code или ошибки в параметры
 * 4. на странице шаблона (app/views/social_login/apple.html.erb) скрипт считывает code или ошибки и отправляет их по Broadcast каналу
 * 5. хук передает полученные данные вызываемому методу.
 * */
export const useAppleAuth = () => {
  /** ссылка для авторизации в Apple */
  /** полученный от Apple код авторизации */
  const authCodeRef = useRef<AppleAuthResult | undefined>();
  const authChannel = useRef<BroadcastChannel>();

  const resetAuthCodeRef = (): void => {
    authCodeRef.current = undefined;
  };

  /** открыть окно для авторизации через Apple */
  const openAuthPopup = (url: string): Window | null =>
    window.open(url, '_blank', 'popup');

  /** прослушивание закрытия окна авторизации Apple */
  const createCloseAuthPopupListener =
    (onError: AppleAuthOnError) =>
    (instance: Window | null): void => {
      if (!instance) {
        return;
      }
      const intID = setInterval(() => {
        if (instance?.closed) {
          clearInterval(intID);
          if (!authCodeRef.current || !authCodeRef.current?.success) {
            onError();
          }
        }
      }, 500);
    };

  /** обработка ответа из результирующего окна авторизации через Apple */
  const createAuthChannelResultHandler =
    (onSuccess: AppleAuthOnSuccess, onError: AppleAuthOnError) =>
    (result: AppleAuthResult) => {
      if (!result?.success) {
        // eslint-disable-next-line no-console
        console.error('APPLE AUTH NOT SUCCESS: ', result);
        onError();
        return;
      }
      authCodeRef.current = result;
      onSuccess(result);
    };

  /**
   * Открыть канал для общения между окном инициатором авторизации
   * и окном в котором происходит получение кода от Apple
   * */
  const openAuthChannel = (handler) => {
    const channel = new BroadcastChannel('auth-apple-code');
    authChannel.current = channel;
    channel.addEventListener('message', handler);
  };

  const closeAuthChannel = () => {
    authChannel.current?.close();
  };

  const handleAppleLogin = (
    config: AppleAuthConfig,
    onSuccess: AppleAuthOnSuccess,
    onError: AppleAuthOnError,
  ): void => {
    try {
      closeAuthChannel();
      resetAuthCodeRef();

      const popup = openAuthPopup(createAppleAuthUrl(config));
      const listenCloseAuthPopup = createCloseAuthPopupListener(onError);
      listenCloseAuthPopup(popup);

      openAuthChannel(createAuthChannelResultHandler(onSuccess, onError));
    } catch (e) {
      console.error(e);
    }
  };

  /** закрытие канала обмена сообщениями */
  useEffect(() => closeAuthChannel, []);

  return {
    handleAppleLogin,
  };
};
